import { SpinnerMask } from '@atlassian/amkt-common';
import { useFeatureFlags } from '@atlassian/mpac-feature-flags';
import { LineChart } from '@atlassian/mpac-ui';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { abbreviateMoney, abbreviateNumber } from '../../../utils/numberFormatting.utils';
import { findNoOfDaysBetweenDataPoints, getXLabelFormatterForDayWidth, LineChartWrapper } from '../Common/ChartUtils';
import ErrorMessage from '../Common/ErrorMessage';
import ReportChartTooltip from '../ReportChartTooltip';
import ChartControls from '../ReportsFilters/ChartControls';
import { useRowClick } from '../useReportsRowClick';
import { useSalesOrEvaluationsReport } from './hooks';
import { deriveLinePatternMapper, getChartData, getChartTooltipPairs, getSeriesByAppName } from './SalesOrEvaluations.utils';
import SalesOrEvaluationsSummary from './SalesOrEvaluationsSummary/SalesOrEvaluationsSummary';
import SalesOrEvaluationsTable from './SalesOrEvaluationsTable';
const getChartToolTip = (reportName, reportBy) => {
  // eslint-disable-next-line react/display-name
  return _ref => {
    let {
      active,
      label,
      payload,
      lines
    } = _ref;
    if (active && payload && payload.length) {
      const pairs = getChartTooltipPairs(reportName, payload, lines);
      const labelWidth = reportBy === 'country' ? '190px' : undefined;
      const valueWidth = reportName === 'sales' ? '140px' : undefined;
      return /*#__PURE__*/React.createElement(ReportChartTooltip, {
        labelWidth: labelWidth,
        valueWidth: valueWidth,
        head: label,
        pairs: pairs
      });
    }
    return null;
  };
};
const SalesOrEvaluations = props => {
  var _props$queryParams, _props$queryParams$pe;
  const {
    queryParams,
    reportName,
    reportingApps
  } = props;
  const {
    salesOrEvaluations,
    pending,
    error
  } = useSalesOrEvaluationsReport(props);
  const {
    rowClick
  } = useRowClick(props.partnerId, props.reportName);
  const reportBy = queryParams.metric ? String(queryParams.metric) : 'hosting';
  const units = queryParams.units ? String(queryParams.units) : 'revenue';
  const yLabelFormat = value => {
    if (reportName === 'sales') {
      return units === 'revenue' ? abbreviateMoney(value) : abbreviateNumber(value);
    }
    return abbreviateNumber(value);
  };
  const emptyChartData = {
    lines: [],
    data: []
  };
  let chartData = emptyChartData;
  const LinePatternMapper = deriveLinePatternMapper(reportName, reportBy, salesOrEvaluations ? getSeriesByAppName(salesOrEvaluations, queryParams.addon).series : [], props.linePatternsUsageUserPreference.value);
  const {
    enableReportsAccessibilityChanges
  } = useFeatureFlags();
  if (salesOrEvaluations && salesOrEvaluations.total.series.length > 0) {
    const totalSeries = getSeriesByAppName(salesOrEvaluations, queryParams.addon).series;
    chartData = totalSeries.length > 0 ? getChartData(totalSeries, reportName, reportBy, units, LinePatternMapper) : emptyChartData;
  }
  const noOfDaysBetweenDataPoints = findNoOfDaysBetweenDataPoints(chartData.data);

  // X-axis for charts contains date strings
  const xLabelFormat = value => getXLabelFormatterForDayWidth(noOfDaysBetweenDataPoints)(value.toString());
  const chartTooltip = getChartToolTip(reportName, reportBy);
  return error ? /*#__PURE__*/React.createElement(ErrorMessage, {
    error: error.error,
    status: error.status
  }) : /*#__PURE__*/React.createElement(SpinnerMask, {
    active: pending,
    size: 50
  }, !pending && salesOrEvaluations && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SalesOrEvaluationsSummary, {
    totalSeries: getSeriesByAppName(salesOrEvaluations, queryParams.addon),
    reportName: reportName,
    reportBy: reportBy,
    period: (props === null || props === void 0 ? void 0 : (_props$queryParams = props.queryParams) === null || _props$queryParams === void 0 ? void 0 : (_props$queryParams$pe = _props$queryParams.period) === null || _props$queryParams$pe === void 0 ? void 0 : _props$queryParams$pe.toString()) || '',
    LinePatternMapper: LinePatternMapper
  }), chartData.data.length > 0 && /*#__PURE__*/React.createElement(LineChartWrapper, {
    enableReportsAccessibilityChanges: enableReportsAccessibilityChanges
  }, /*#__PURE__*/React.createElement(LineChart, {
    lines: chartData.lines,
    data: chartData.data,
    xLabelFormat: xLabelFormat,
    yLabelFormat: yLabelFormat,
    tooltip: chartTooltip,
    isChartDeEmphasised: props.linePatternsUsageUserPreference.isUpdateLinePatternUsageUserPreferencePending
  }), /*#__PURE__*/React.createElement(ChartControls, {
    showDisplayByFilter: reportName === 'sales',
    linePatternsUsageUserPreference: {
      value: props.linePatternsUsageUserPreference.value,
      onPreferenceChange: props.linePatternsUsageUserPreference.onPreferenceChange
    }
  })), salesOrEvaluations.total.series.length > 0 && /*#__PURE__*/React.createElement(SalesOrEvaluationsTable, {
    reportApps: salesOrEvaluations.addons,
    reportName: reportName,
    reportBy: reportBy,
    allReportingApps: reportingApps,
    onRowClick: rowClick,
    totalSeries: salesOrEvaluations.total,
    selectedAppKey: props.queryParams.addon
  })));
};
export default /*#__PURE__*/React.memo(SalesOrEvaluations, isEqual);